import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Globe, Check, Search } from 'lucide-react';
import { useLanguage } from './LanguageProvider';
import { useTranslation } from 'react-i18next';

interface LanguageSwitcherProps {
  variant?: 'desktop' | 'mobile';
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'desktop',
  className = '' 
}) => {
  const { language, setLanguage, languages, isRTL } = useLanguage();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const currentLanguage = useMemo(
    () => languages.find(l => l.code === language) || languages[0],
    [language, languages]
  );

  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) return languages;
    const query = searchQuery.toLowerCase();
    return languages.filter(
      l => l.name.toLowerCase().includes(query) || 
           l.nativeName.toLowerCase().includes(query) ||
           l.code.toLowerCase().includes(query)
    );
  }, [languages, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleLanguageSelect = (code: string) => {
    setLanguage(code);
    setIsOpen(false);
    setSearchQuery('');
  };

  if (variant === 'mobile') {
    return (
      <div className={`space-y-2 ${className}`}>
        <span className="text-[13px] font-medium text-ink-2">
          {t('language.title')}
        </span>
        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
          {languages.slice(0, 9).map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`px-2 py-1.5 text-xs rounded-full transition-colors ${
                language === lang.code
                  ? 'bg-accent/[0.12] text-accent font-medium'
                  : 'text-ink-2 hover:bg-card'
              }`}
            >
              {lang.nativeName}
            </button>
          ))}
        </div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {isOpen ? t('common.close') : `+${languages.length - 9} ${t('common.more') || 'more'}`}
        </button>
        {isOpen && (
          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pt-2 border-t border-slate-200 dark:border-zinc-700">
            {languages.slice(9).map((lang) => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className={`px-2 py-1.5 text-xs rounded-full text-start transition-colors ${
                  language === lang.code
                    ? 'bg-accent/[0.12] text-accent font-medium'
                    : 'text-ink-2 hover:bg-card'
                }`}
              >
                {lang.nativeName}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex h-8 min-w-8 items-center justify-center gap-1 rounded-full ps-2 pe-2.5 text-ink-2 transition-colors hover:text-ink focus:outline-none ${isOpen ? 'bg-card text-ink' : ''}`}
        aria-label={t('language.select')}
        aria-expanded={isOpen}
        title={currentLanguage.nativeName}
      >
        <Globe className="h-[15px] w-[15px]" />
        <span className="text-[11.5px] font-semibold tracking-wide">{currentLanguage.code.split('-')[0].toUpperCase()}</span>
      </button>

      {isOpen && (
        <div
          className={`absolute top-full ${isRTL ? 'left-0' : 'right-0'} mt-2 w-[240px] rounded-[18px] bg-raised p-2 shadow-pop z-50 dropdown-animate`}
        >
          <div className="relative mb-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-3" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('common.search')}
              className="w-full rounded-[12px] bg-card ps-8 pe-3 py-2 text-[13.5px] text-ink placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-accent/30"
            />
          </div>

          <div className="max-h-72 overflow-y-auto">
            {filteredLanguages.length === 0 ? (
              <p className="px-3.5 py-2 text-[13px] text-ink-3">
                {t('transcription.search.noResults')}
              </p>
            ) : (
              filteredLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className={`flex w-full items-center justify-between gap-3 rounded-[12px] px-3.5 py-1.5 text-start transition-colors hover:bg-card ${
                    language === lang.code ? 'text-ink' : 'text-ink-2 hover:text-ink'
                  }`}
                >
                  <span className="min-w-0">
                    <span className={`block text-[14px] ${language === lang.code ? 'font-medium' : ''}`}>{lang.nativeName}</span>
                    {lang.nativeName !== lang.name && (
                      <span className="block text-[12px] text-ink-3">{lang.name}</span>
                    )}
                  </span>
                  {language === lang.code && <Check className="h-4 w-4 shrink-0 text-accent" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
